import {
  AppShell,
  Badge,
  BrandLockup,
  BrandLogotype,
  BrandSymbol,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Header,
  Input,
  Menu,
  Modal,
  Radio,
  SedeSelector,
  Select,
  Sidebar,
  Spinner,
  Table,
  Tabs,
  useToast,
} from '@classgo/ui';
import { useState } from 'react';

const NAV = [
  {
    items: [
      { href: '/', label: 'Inicio' },
      { href: '/playground', label: 'Playground' },
    ],
  },
  {
    title: 'Académico',
    items: [
      { href: '/students', label: 'Alumnos' },
      { href: '/groups', label: 'Grupos' },
    ],
  },
];

type Group = { id: string; name: string; students: number; jornada: string };
const GROUPS: Group[] = [
  { id: '1', name: 'Primero A', students: 24, jornada: 'Mañana' },
  { id: '2', name: 'Primero B', students: 22, jornada: 'Tarde' },
  { id: '3', name: 'Segundo A', students: 23, jornada: 'Mañana' },
];

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="space-y-3">
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {children}
  </section>
);

/**
 * Every component of the design system in every state, on one page. It is what the gate's
 * Playwright smoke opens, and what a designer reviews when a token changes.
 */
export const Playground = () => {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [sede, setSede] = useState('a');
  const [navOpen, setNavOpen] = useState(false);

  return (
    <AppShell
      navigationOpen={navOpen}
      onCloseNavigation={() => setNavOpen(false)}
      sidebar={
        <Sidebar
          brand={<BrandLockup height={26} />}
          sections={NAV}
          currentPath="/playground"
          renderLink={(item, className) => (
            <a href={item.href} className={className}>
              {item.label}
            </a>
          )}
          footer={
            <SedeSelector
              sedes={[
                { id: 'a', name: 'Principal', isMain: true },
                { id: 'b', name: 'Norte' },
              ]}
              selectedId={sede}
              onSelect={setSede}
              onAdd={() =>
                toast.show({ title: 'Agregar sede', description: 'Llega con #18', tone: 'info' })
              }
            />
          }
        />
      }
      header={
        <Header
          title="Playground del design system"
          onOpenNavigation={() => setNavOpen(true)}
          actions={
            <>
              <Badge tone="brand">Owner</Badge>
              <Menu
                trigger="rectoria@demo.classgo.co ▾"
                label="Cuenta"
                items={[
                  {
                    id: 'profile',
                    label: 'Mi perfil',
                    onSelect: () => toast.show({ title: 'Perfil' }),
                  },
                  {
                    id: 'logout',
                    label: 'Salir',
                    onSelect: () => toast.show({ title: 'Sesión cerrada', tone: 'success' }),
                    danger: true,
                  },
                ]}
              />
            </>
          }
        />
      }
    >
      <div className="mx-auto max-w-5xl space-y-10">
        <h1 className="text-2xl font-semibold text-gray-900">Componentes</h1>

        <Section title="Marca">
          <Card>
            <div className="flex flex-wrap items-center gap-6">
              <BrandSymbol size={16} />
              <BrandSymbol size={32} />
              <BrandSymbol size={64} />
              <BrandLogotype height={28} />
              <BrandLockup height={32} />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-6 rounded-card bg-gray-900 p-4">
              <BrandLogotype variant="dark" height={28} />
              <BrandLockup variant="dark" height={32} />
            </div>
            <p className="mt-4 text-sm text-gray-500">
              <BrandLogotype
                variant="mono"
                height={16}
                decorative
                className="inline align-middle"
              />{' '}
              en el color del texto (variante mono).
            </p>
          </Card>
        </Section>
        <Section title="Botones">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Primario</Button>
            <Button variant="secondary">Secundario</Button>
            <Button variant="danger">Eliminar</Button>
            <Button variant="ghost">Ghost</Button>
            <Button loading>Guardando</Button>
            <Button disabled>Deshabilitado</Button>
            <Button size="sm">Pequeño</Button>
            <Button size="lg">Grande</Button>
          </div>
        </Section>

        <Section title="Formularios">
          <Card>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Nombre del alumno"
                placeholder="Mariana Rodríguez"
                help="Como aparece en el documento"
              />
              <Input label="Correo" type="email" error="Correo inválido" defaultValue="mariana@" />
              <Select
                label="Grado"
                placeholder="Selecciona un grado"
                defaultValue=""
                options={[
                  { value: '1', label: 'Primero' },
                  { value: '2', label: 'Segundo' },
                ]}
              />
              <Input label="Deshabilitado" disabled defaultValue="No editable" />
              <div className="space-y-2">
                <Checkbox
                  label="Acepta el tratamiento de datos"
                  description="Ley 1581 de 2012"
                  defaultChecked
                />
                <Checkbox label="Consentimiento de IA" description="Necesario para MentorIA" />
              </div>
              <div className="space-y-2">
                <Radio name="jornada" label="Mañana" defaultChecked />
                <Radio name="jornada" label="Tarde" />
              </div>
            </div>
          </Card>
        </Section>

        <Section title="Señales">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Gris</Badge>
            <Badge tone="brand">Brand</Badge>
            <Badge tone="success">Activo</Badge>
            <Badge tone="warning">Pendiente</Badge>
            <Badge tone="danger">Suspendido</Badge>
            <Badge tone="info">Info</Badge>
            <Badge tone="accent">Demo</Badge>
            <Spinner />
          </div>
        </Section>

        <Section title="Tabla">
          <Card flush>
            <Table
              caption="Grupos del año 2027"
              columns={[
                {
                  key: 'name',
                  header: 'Grupo',
                  cell: (row) => row.name,
                  sortable: true,
                  sortValue: (row) => row.name,
                },
                {
                  key: 'jornada',
                  header: 'Jornada',
                  cell: (row) => <Badge tone="gray">{row.jornada}</Badge>,
                },
                {
                  key: 'students',
                  header: 'Alumnos',
                  cell: (row) => row.students,
                  sortable: true,
                  sortValue: (row) => row.students,
                  align: 'right',
                },
              ]}
              rows={GROUPS}
              rowKey={(row) => row.id}
            />
          </Card>
          <Card flush>
            <Table
              caption="Tabla vacía"
              columns={[{ key: 'x', header: 'Nombre', cell: () => null }]}
              rows={[]}
              rowKey={() => ''}
              empty={
                <EmptyState
                  title="Aún no hay grupos"
                  description="Crea el primero desde Grupos."
                  action={<Button size="sm">Nuevo grupo</Button>}
                />
              }
            />
          </Card>
        </Section>

        <Section title="Tabs">
          <Card>
            <Tabs
              label="Secciones de ejemplo"
              items={[
                {
                  id: 'a',
                  label: 'Alumnos',
                  content: <p className="text-sm text-gray-600">Lista de alumnos.</p>,
                },
                {
                  id: 'b',
                  label: 'Docentes',
                  content: <p className="text-sm text-gray-600">Lista de docentes.</p>,
                },
              ]}
            />
          </Card>
        </Section>

        <Section title="Modal y avisos">
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setModalOpen(true)}>Abrir modal</Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast.show({
                  title: 'Guardado',
                  description: 'La sede quedó registrada',
                  tone: 'success',
                })
              }
            >
              Toast de éxito
            </Button>
            <Button
              variant="secondary"
              onClick={() => toast.show({ title: 'No se pudo guardar', tone: 'error' })}
            >
              Toast de error
            </Button>
          </div>
          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Nueva sede"
            description="Cada sede tiene su dirección y su código DANE."
            actions={
              <>
                <Button variant="secondary" onClick={() => setModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => setModalOpen(false)}>Guardar</Button>
              </>
            }
          >
            <div className="space-y-4">
              <Input label="Nombre" placeholder="Sede Norte" />
              <Input label="Dirección" placeholder="Calle 45 # 12-30" />
            </div>
          </Modal>
        </Section>
      </div>
    </AppShell>
  );
};
